import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Snackbar,
    Grid,
    Card,
    CardContent,
    TextField,
    Chip,
    FormControl, // NEW: Import for filter controls
    InputLabel,  // NEW: Import for filter controls
    Select,      // NEW: Import for filter controls
    MenuItem     // NEW: Import for filter controls
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export default function SelectLevelProducts({ db, title, onSave, onCancel }) {
    // State hooks
    const [allProducts, setAllProducts] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    // NEW: State for filters
    const [selectedType, setSelectedType] = useState('all');
    const [selectedGst, setSelectedGst] = useState('all');
    const [selectedPriceRange, setSelectedPriceRange] = useState('all');

    // NEW: Options for the filter dropdowns
    const typeOptions = [
        { value: 'all', label: 'All Types' },
        { value: 'standard', label: 'Standard' },
        { value: 'derived', label: 'Derived' }
    ];

    const gstOptions = [
        { value: 'all', label: 'All GST' },
        { value: 5, label: '5%' },
        { value: 12, label: '12%' },
        { value: 18, label: '18%' },
        { value: 28, label: '28%' }
    ];

    const priceRanges = [
        { value: 'all', label: 'All Prices' },
        { value: '0-500', label: '₹0 - ₹500' },
        { value: '501-2000', label: '₹501 - ₹2000' },
        { value: '2001-5000', label: '₹2001 - ₹5000' },
        { value: '5001+', label: 'Over ₹5000' }
    ];

    useEffect(() => {
        const fetchAllProducts = async () => {
            setLoading(true);
            try {
                const catalogueCollection = collection(db, "Catalogue");
                const catalogueSnapshot = await getDocs(catalogueCollection);
                const productsList = catalogueSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    label: doc.data().name
                }));
                setAllProducts(productsList);
            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAllProducts();
    }, [db]);

    // MODIFIED: This memoized value now filters products based on selected filter states
    const autocompleteOptions = useMemo(() => {
        let filteredProducts = [...allProducts];

        // Filter by type
        if (selectedType !== 'all') {
            filteredProducts = filteredProducts.filter(p =>
                selectedType === 'standard' ? p.isStandard === true : p.isStandard === false
            );
        }

        // Filter by GST
        if (selectedGst !== 'all') {
            filteredProducts = filteredProducts.filter(p => p.gst === selectedGst);
        }

        // Filter by Price
        if (selectedPriceRange !== 'all') {
            if (selectedPriceRange.includes('+')) {
                const minPrice = parseInt(selectedPriceRange.replace('+', ''), 10);
                filteredProducts = filteredProducts.filter(p => p.price >= minPrice);
            } else {
                const [minPrice, maxPrice] = selectedPriceRange.split('-').map(Number);
                filteredProducts = filteredProducts.filter(p => p.price >= minPrice && p.price <= maxPrice);
            }
        }

        return filteredProducts;
    }, [allProducts, selectedType, selectedGst, selectedPriceRange]);


    const selectedProductsForPreview = useMemo(() => allProducts.filter(p => selectedProductIds.includes(p.id)), [allProducts, selectedProductIds]);

    const handleConfirm = () => {
        if (selectedProductsForPreview.length === 0) {
            setSnackbar({ open: true, message: "Please select at least one product." });
            return;
        }
        onSave(selectedProductsForPreview);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>

                <Grid container spacing={4} sx={{ mt: 1 }}>
                    {/* Left Panel: Product Selection */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={2} sx={{ height: "100%" }}>
                            <CardContent>
                                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>Select Products</Typography>

                                {/* NEW: Filter controls */}
                                <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={selectedType}
                                            label="Type"
                                            onChange={(e) => setSelectedType(e.target.value)}
                                        >
                                            {typeOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>GST</InputLabel>
                                        <Select
                                            value={selectedGst}
                                            label="GST"
                                            onChange={(e) => setSelectedGst(e.target.value)}
                                        >
                                            {gstOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Price</InputLabel>
                                        <Select
                                            value={selectedPriceRange}
                                            label="Price"
                                            onChange={(e) => setSelectedPriceRange(e.target.value)}
                                        >
                                            {priceRanges.map(option => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect={true}
                                    options={autocompleteOptions}
                                    loading={loading}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={selectedProductsForPreview}
                                    onChange={(event, newValue) => {
                                        setSelectedProductIds(newValue.map(product => product.id));
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Search and select products..." size="small" />
                                    )}
                                    renderOption={(props, option) => {
                                        const borderColor = option.isStandard ? 'green' : 'orange';
                                        return (
                                            <li {...props}>
                                                <Box sx={{ borderLeft: `4px solid ${borderColor}`, pl: 1, width: '100%' }}>
                                                    <Typography variant="body2">{option.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Price: ₹{option.price || 0}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        );
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip variant="outlined" label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
                                        ))
                                    }
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Panel: Selected Product Details */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={2} sx={{ height: "100%" }}>
                            <CardContent>
                                <Typography variant="h6" color="secondary" sx={{ mb: 2 }}>
                                    Selected Details ({selectedProductsForPreview.length})
                                </Typography>
                                <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                                    {selectedProductsForPreview.length > 0 ? (
                                        selectedProductsForPreview.map(product => {
                                            const borderColor = product.isStandard ? 'green' : 'orange';
                                            return (
                                                <Paper key={product.id} variant="outlined" sx={{ p: 2, mb: 1, borderLeft: `5px solid ${borderColor}` }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{product.name}</Typography>
                                                    <Typography variant="body2"><strong>Price:</strong> ₹{product.price || 'N/A'}</Typography>
                                                    <Typography variant="body2"><strong>HSN:</strong> {product.hsn || 'N/A'}</Typography>
                                                    <Typography variant="body2"><strong>GST:</strong> {product.gst || 'N/A'}%</Typography>
                                                    <Typography variant="body2"><strong>MOQ:</strong> {product.moq || 'N/A'}</Typography>

                                                    {!product.isStandard && product.components && product.components.length > 0 && (
                                                        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #eee' }}>
                                                            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'text.secondary', mr: 1 }}>
                                                                Components:
                                                            </Typography>
                                                            <Typography variant="body2" component="span" color="text.secondary">
                                                                {product.components.map(c => c.name).join(', ')}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            );
                                        })
                                    ) : (
                                        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>
                                            Details of selected products will appear here.
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Final Action Buttons */}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
                    <Button variant="contained" color="success" onClick={handleConfirm} disabled={selectedProductsForPreview.length === 0}>
                        Confirm Selection
                    </Button>
                    <Button variant="outlined" color="error" onClick={onCancel}>
                        Cancel
                    </Button>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Container>
    );
}